import React, { useState } from "react";
import axios from "axios";
import { CheckCircle, Loader2, Tags, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import { ChangeHistory } from "./ChangeHistory";

const API = process.env.REACT_APP_BACKEND_URL;
const BASE = `${API}/api/super-admin/owner-actions`;
const opts = { withCredentials: true };
const errText = (e) => e?.response?.data?.detail || e.message || "Something went wrong";
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const Shell = ({ icon: Icon, title, blurb, testid, children }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-3" data-testid={testid}>
    <div className="flex items-start gap-3">
      <Icon className="h-5 w-5 text-slate-700 mt-0.5" />
      <div>
        <h3 className="font-semibold text-slate-900">{title}</h3>
        <p className="text-sm text-slate-600">{blurb}</p>
      </div>
    </div>
    {children}
  </div>
);

const Done = ({ text, testid }) => (
  <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid={testid}>
    <CheckCircle className="h-4 w-4" /> {text}
  </p>
);

export const CatalogNamesCard = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const run = async (apply) => {
    setBusy(true); setErr(null);
    try {
      if (apply) {
        const r = await axios.post(`${BASE}/catalog-names/apply`, {}, opts);
        setMsg(`${r.data.updated} product name(s) updated. A backup of the old names was saved.`);
        setData(null);
      } else {
        setData((await axios.get(`${BASE}/catalog-names/preview`, opts)).data); setMsg(null);
      }
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };

  return (
    <Shell icon={Tags} testid="catalog-names-card" title="Refresh product names in the Quote Generator"
      blurb="Shows every screener and booklet product whose name still uses old wording, with the new name next to it.">
      <Button size="sm" variant="outline" onClick={() => run(false)} disabled={busy} data-testid="catalog-names-preview-btn">
        {busy && !data ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Show what would change
      </Button>
      {data && data.count === 0 && <Done testid="catalog-names-none" text="All product names are already up to date." />}
      {data && data.count > 0 && (
        <div className="space-y-2">
          <div className="max-h-72 overflow-auto rounded border border-slate-100" data-testid="catalog-names-list">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-left"><tr><th className="p-2">Old name</th><th className="p-2">New name</th></tr></thead>
              <tbody>{data.changes.map((c) => (
                <tr key={c.sku} className="border-t border-slate-100" data-testid={`catalog-row-${c.sku}`}>
                  <td className={`p-2 text-slate-500 ${c.old_name !== c.new_name ? "line-through" : ""}`}>{c.old_name}</td>
                  <td className="p-2 text-slate-900">
                    {c.old_name === c.new_name ? <span className="italic text-slate-600">Same name; description wording updated</span> : c.new_name}
                  </td>
                </tr>))}
              </tbody>
            </table>
          </div>
          <Button size="sm" onClick={() => run(true)} disabled={busy} data-testid="catalog-names-apply-btn">
            Update {data.count} name(s)
          </Button>
        </div>
      )}
      {msg && <Done testid="catalog-names-done" text={msg} />}
      {err && <p className="text-sm text-red-700" data-testid="catalog-names-error">{err}</p>}
      <ChangeHistory kind="catalog_name" onUndone={() => setData(null)} refreshKey={msg} />
    </Shell>
  );
};

const QuoteRow = ({ q, checked, onToggle }) => (
  <div className="rounded border border-slate-200 p-3 text-xs space-y-1" data-testid={`quote-row-${q.quote_number}`}>
    <label className="flex items-center gap-2 font-medium text-slate-900">
      <input type="checkbox" checked={checked} onChange={onToggle} data-testid={`quote-check-${q.quote_number}`} />
      {q.quote_number} · {q.school_name || "—"} · {q.status} · total {money(q.subtotal)} (stays the same)
    </label>
    <div className="pl-6 text-slate-500">Now: {q.old_items.map((i) => `${i.sku} ×${i.quantity}`).join(", ")}</div>
    <div className="pl-6 text-slate-800">Becomes: {q.new_items.map((i) => `${i.sku} ×${i.quantity} @ ${money(i.unit_price)}`).join(", ")}</div>
    {q.has_over_limit && (
      <div className="pl-6 flex items-center gap-1 text-amber-700" data-testid={`quote-over-limit-${q.quote_number}`}>
        <AlertTriangle className="h-3 w-3" /> Keeping the old total puts some lines over $250, so this quote would still hit the sole-vendor limit.
      </div>
    )}
  </div>
);

export const QuoteMigrationCard = () => {
  const [data, setData] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const preview = async () => {
    setBusy(true); setErr(null); setMsg(null);
    try { const d = (await axios.get(`${BASE}/quote-migration/preview`, opts)).data; setData(d); setPicked([]); }
    catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await axios.post(`${BASE}/quote-migration/apply`, { quote_numbers: picked }, opts);
      setMsg(`${r.data.migrated.length} quote(s) moved. A backup of each original quote was saved.`); setData(null);
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  };
  const toggle = (n) => setPicked((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));

  return (
    <Shell icon={FileSpreadsheet} testid="quote-migration-card" title="Move open quotes to the new grade-band codes"
      blurb="Lists open and sent-but-unpaid quotes that still use old codes. Each quote keeps its original total. Nothing changes until you tick quotes and press Move.">
      <Button size="sm" variant="outline" onClick={preview} disabled={busy} data-testid="quote-migration-preview-btn">
        {busy && !data ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Show quotes that would move
      </Button>
      {data && data.count === 0 && <Done testid="quote-migration-none" text="No open quotes use old codes." />}
      {data && data.count > 0 && (
        <div className="space-y-2" data-testid="quote-migration-list">
          {data.quotes.map((q) => (
            <QuoteRow key={q.quote_number} q={q} checked={picked.includes(q.quote_number)} onToggle={() => toggle(q.quote_number)} />
          ))}
          <Button size="sm" onClick={apply} disabled={busy || !picked.length} data-testid="quote-migration-apply-btn">
            Move {picked.length} selected quote(s)
          </Button>
        </div>
      )}
      {msg && <Done testid="quote-migration-done" text={msg} />}
      {err && <p className="text-sm text-red-700" data-testid="quote-migration-error">{err}</p>}
      <ChangeHistory kind="quote_items" onUndone={() => setData(null)} refreshKey={msg} />
    </Shell>
  );
};
